import { useState } from "react";
import axios from "axios";
import { Download, Music, Film, Loader2, Search, Clock, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDuration = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes > 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes > 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e3).toFixed(0)} KB`;
};

const YouTubeDownloaderTab = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState("");
  const [videoInfo, setVideoInfo] = useState(null);

  const token = localStorage.getItem("auth_token");

  const fetchFormats = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setError("");
    setVideoInfo(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/youtube-dl/formats`, { url: trimmed }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVideoInfo(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to fetch video info");
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (pasted && /youtu/.test(pasted)) {
      setUrl(pasted);
      setTimeout(() => {
        setError("");
        setVideoInfo(null);
        setLoading(true);
        axios.post(`${API}/api/youtube-dl/formats`, { url: pasted.trim() }, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data }) => setVideoInfo(data))
          .catch((e) => setError(e.response?.data?.detail || "Failed to fetch video info"))
          .finally(() => setLoading(false));
      }, 0);
    }
  };

  const download = async (type, formatId, bitrate, label) => {
    if (!videoInfo) return;
    const key = `${type}-${label}`;
    setDownloading(key);
    try {
      const endpoint = type === "mp4"
        ? `${API}/api/youtube-dl/download/mp4/${videoInfo.video_id}?format_id=${encodeURIComponent(formatId)}`
        : `${API}/api/youtube-dl/download/mp3/${videoInfo.video_id}?bitrate=${bitrate}`;

      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download failed");
      }

      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = type === "mp4" ? "mp4" : "mp3";
      const title = (videoInfo.title || "download").replace(/[^\w\s-]/g, "").trim().substring(0, 80);
      a.download = `${title}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(e.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="youtube-downloader-tab">
      {/* URL Input */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">YouTube Downloader</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => e.key === "Enter" && fetchFormats()}
            placeholder="Paste a YouTube link..."
            className="flex-1 bg-black border border-zinc-700 text-white px-4 py-3 rounded-lg focus:border-orange-500/50 focus:outline-none text-sm"
            data-testid="yt-url-input"
          />
          <button
            onClick={fetchFormats}
            disabled={loading || !url.trim()}
            className="px-5 py-3 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors shrink-0"
            data-testid="yt-fetch-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Fetch
          </button>
        </div>
        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-400 text-sm" data-testid="yt-error">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      )}

      {/* Results */}
      {videoInfo && !loading && (
        <div className="space-y-6">
          {/* Video Info */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 flex gap-5 items-start">
            <img
              src={videoInfo.thumbnail}
              alt=""
              className="w-48 aspect-video object-cover rounded-lg shrink-0 hidden sm:block"
            />
            <div className="min-w-0">
              <h4 className="text-white font-bold text-base leading-snug line-clamp-2" data-testid="yt-video-title">
                {videoInfo.title}
              </h4>
              {videoInfo.duration > 0 && (
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs mt-2">
                  <Clock className="w-3.5 h-3.5" /> {formatDuration(videoInfo.duration)}
                </div>
              )}
            </div>
          </div>

          {/* MP4 Downloads */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Film className="w-5 h-5 text-orange-500" />
              <h4 className="text-white font-bold text-sm uppercase tracking-wider">MP4 — Video</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {videoInfo.mp4_formats.map((f, i) => {
                const key = `mp4-${f.quality}`;
                const isActive = downloading === key;
                const isBest = i === 0;
                return (
                  <button
                    key={f.format_id}
                    onClick={() => download("mp4", f.format_id, null, f.quality)}
                    disabled={!!downloading}
                    className={`flex items-center justify-between gap-2 px-4 py-3 border rounded-lg transition-colors text-left group disabled:opacity-40 ${
                      isBest 
                        ? "bg-orange-500/10 border-orange-500/40 hover:border-orange-400 hover:bg-orange-500/15" 
                        : "bg-black/50 border-zinc-700 hover:border-orange-500/50 hover:bg-orange-500/5"
                    }`}
                    data-testid={`download-mp4-${f.quality}`}
                  >
                    <div>
                      <span className="text-white font-bold text-sm">{f.quality}</span>
                      {isBest && <span className="text-orange-400 text-[10px] font-bold uppercase ml-1.5">Best</span>}
                      {f.filesize && (
                        <span className="text-zinc-600 text-xs block">{formatFileSize(f.filesize)}</span>
                      )}
                    </div>
                    {isActive ? (
                      <Loader2 className="w-4 h-4 text-orange-500 animate-spin shrink-0" />
                    ) : (
                      <Download className={`w-4 h-4 shrink-0 transition-colors ${isBest ? "text-orange-500" : "text-zinc-600 group-hover:text-orange-500"}`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* MP3 Downloads */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Music className="w-5 h-5 text-orange-500" />
              <h4 className="text-white font-bold text-sm uppercase tracking-wider">MP3 — Audio</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {videoInfo.mp3_formats.map((f) => {
                const key = `mp3-${f.quality}`;
                const isActive = downloading === key;
                return (
                  <button
                    key={f.bitrate}
                    onClick={() => download("mp3", null, f.bitrate, f.quality)}
                    disabled={!!downloading}
                    className="flex items-center justify-between gap-2 px-4 py-3 bg-black/50 border border-zinc-700 hover:border-orange-500/50 hover:bg-orange-500/5 disabled:opacity-40 rounded-lg transition-colors text-left group"
                    data-testid={`download-mp3-${f.quality}`}
                  >
                    <span className="text-white font-bold text-sm">{f.quality}</span>
                    {isActive ? (
                      <Loader2 className="w-4 h-4 text-orange-500 animate-spin shrink-0" />
                    ) : (
                      <Download className="w-4 h-4 text-zinc-600 group-hover:text-orange-500 shrink-0 transition-colors" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeDownloaderTab;
